import { contrastRatio } from './contrast';
/** The 12 editorial object colours (spec §14, direction A); ids are stable, documents keep plain hexes. */
export const OBJECT_COLORS = [
  { id: 'yellow', hex: '#ffe27a' },
  { id: 'orange', hex: '#ffc48a' },
  { id: 'red', hex: '#ff9b8f' },
  { id: 'pink', hex: '#ffb8cf' },
  { id: 'purple', hex: '#d4c2ff' },
  { id: 'blue', hex: '#a9d4ff' },
  { id: 'teal', hex: '#9be3d6' },
  { id: 'green', hex: '#bdebb0' },
  { id: 'sand', hex: '#e9dcc6' },
  { id: 'gray', hex: '#d9d6d0' },
  { id: 'white', hex: '#ffffff' },
  { id: 'black', hex: '#1a1814' },
] as const;
export type ObjectColorId = (typeof OBJECT_COLORS)[number]['id'];

/** Hexes of the pre-P11 palette: objects keep them, the panel still shows their swatch as chosen. */
export const LEGACY_HEX: Record<string, ObjectColorId> = {
  '#ffe066': 'yellow',
  '#ffb3c7': 'pink',
  '#a5d8ff': 'blue',
  '#b2f2bb': 'green',
  '#ffc078': 'orange',
  '#d0bfff': 'purple',
  '#ffffff': 'white',
  '#141414': 'black',
};

const BY_HEX = new Map<string, ObjectColorId>([...Object.entries(LEGACY_HEX), ...OBJECT_COLORS.map((c) => [c.hex, c.id] as const)]);

export function swatchOf(hex: string): ObjectColorId | null {
  return BY_HEX.get(hex.toLowerCase()) ?? null;
}

export const DEFAULT_STICKY: string = OBJECT_COLORS[0].hex;

/** Ink of a sticky's text: the canvas ink, or white on a dark sticky (the black swatch). */
export function stickyInk(fill: string): string {
  return contrastRatio('#141414', fill) >= contrastRatio('#ffffff', fill) ? '#141414' : '#ffffff';
}

const AUTHOR_INK_DARK = '#44403c';
const AUTHOR_INK_LIGHT = '#a8a29e';

/** Opaque ink of a sticky's author label: the one of two muted inks that reads best on its colour (AA on the palette). */
export function stickyAuthorInk(fill: string): string {
  return contrastRatio(AUTHOR_INK_DARK, fill) >= contrastRatio(AUTHOR_INK_LIGHT, fill) ? AUTHOR_INK_DARK : AUTHOR_INK_LIGHT;
}

const lab = (hex: string): [number, number, number] => {
  const v = (i: number) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = [v(1), v(3), v(5)];
  const f = (t: number) => (t > 216 / 24389 ? Math.cbrt(t) : (24389 / 27 * t + 16) / 116);
  const x = f((0.4124 * r + 0.3576 * g + 0.1805 * b) / 0.95047);
  const y = f(0.2126 * r + 0.7152 * g + 0.0722 * b);
  const z = f((0.0193 * r + 0.1192 * g + 0.9505 * b) / 1.08883);
  return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
};

/** The swatch of `palette` closest to `hex` as the eye sees it (CIE Lab distance); an imported colour (P11b). */
export function nearestColor(hex: string, palette: readonly string[]): string {
  if (!/^#[0-9a-f]{6}$/i.test(hex) || palette.length === 0) return palette[0] ?? hex;
  const [l, a, b] = lab(hex);
  let best = palette[0]!;
  let dist = Infinity;
  for (const p of palette) {
    const [pl, pa, pb] = lab(p);
    const d = (l - pl) ** 2 + (a - pa) ** 2 + (b - pb) ** 2;
    if (d < dist) [best, dist] = [p, d];
  }
  return best;
}
